import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

// ============================================
// ENVIRONMENT DETECTION
// ============================================

const getEnvironment = () => {
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  
  if (hostname.includes('planaidesign-dev')) {
    return 'development';
  }
  
  if (hostname.includes('planaidesign-prod') || 
      hostname.includes('planaidesign.com')) {
    return 'production';
  }
  
  return 'production';
};

const currentEnvironment = getEnvironment();
const isDevelopment = currentEnvironment === 'development';

// ============================================
// FIREBASE CONFIGURATIONS
// ============================================

const developmentConfig = {
  apiKey: "AIzaSyCbEo-efSyC0LfVmAQEh9lOHUBX3nZ5Ilg",
  authDomain: "planaidesign-dev.firebaseapp.com",
  projectId: "planaidesign-dev",
  storageBucket: "planaidesign-dev.firebasestorage.app",
  messagingSenderId: "765772288763",
  appId: "1:765772288763:web:4d2ad2ef93da83dcc6565e"
};

const productionConfig = {
  apiKey: "AIzaSyCrtJO8BAhtpiIS-f23AcYyWJPh7b5lAyQ",
  authDomain: "planaidesign-prod.firebaseapp.com",
  projectId: "planaidesign-prod",
  storageBucket: "planaidesign-prod.firebasestorage.app",
  messagingSenderId: "231434865742",
  appId: "1:231434865742:web:dbc27a39191dd6804a353f"
};

const firebaseConfig = isDevelopment ? developmentConfig : productionConfig;

if (isDevelopment) {
  console.log(`🔥 Firebase Environment: DEVELOPMENT`);
  console.log(`🔥 Project ID: ${firebaseConfig.projectId}`);
  console.log(`🚀 Using Render API v3.0 (Firebase Admin enabled)`);
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// ============================================
// STRIPE CONFIGURATION
// ============================================

const STRIPE_PK_DEV = 'pk_test_51RvHj66BadDDpDfdZCIaxhjnYZvCQ9IAKmDUxe1elVfvgSkaTaVMXIrioCMv4LHI8Y7qFvHDUYfiF2zX55rg3a5C00BTjWvU9o';
const STRIPE_PK_PROD = 'pk_live_51RvHj66BadDDpDfdXbocYnqamV4Nb2Ymll7WWQQvg2cYdFaIIDj6QpBQAG3hPtIoeyml6AjeXBvdiGAgBn5iKT6u00gBcyVYEH';

export const STRIPE_PUBLISHABLE_KEY = isDevelopment ? STRIPE_PK_DEV : STRIPE_PK_PROD;

// ============================================
// RENDER API CONFIGURATION
// ============================================

const RENDER_API_URL = 'https://planaidesign-api.onrender.com';

// Simple API call helper
const callAPI = async (endpoint, data) => {
  try {
    console.log(`Calling API: ${endpoint}`, data);
    
    const response = await fetch(`${RENDER_API_URL}/api/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data),
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || `API call failed: ${response.status}`);
    }
    
    console.log(`API response for ${endpoint}:`, result);
    return { data: result };
  } catch (error) {
    console.error(`Error calling ${endpoint}:`, error);
    throw error;
  }
};

// ============================================
// STAGE 1 CARD CAPTURE FUNCTIONS
// Server handles BOTH Stripe AND Firestore updates
// ============================================

// Step 1: Create SetupIntent
// Server creates Stripe SetupIntent AND updates Firestore (user + project)
export const createSetupIntent = async (data) => {
  const { projectId, userEmail, userName, amount, projectType, userId } = data;
  
  // Check if user already has a Stripe customer ID
  let existingCustomerId = null;
  if (userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists() && userDoc.data().stripeCustomerId) {
        existingCustomerId = userDoc.data().stripeCustomerId;
        console.log('Found existing customer ID:', existingCustomerId);
      }
    } catch (e) {
      console.log('Could not check for existing customer:', e.message);
    }
  }
  
  // Call Render API - server handles Stripe AND Firestore updates
  const result = await callAPI('createSetupIntent', {
    projectId,
    userEmail,
    userName,
    amount,
    projectType,
    userId,
    existingCustomerId
  });
  
  return result;
};

// Step 2: Save payment method after card is confirmed
// Server saves to Stripe AND updates Firestore (project status)
export const savePaymentMethod = async (data) => {
  const { setupIntentId, projectId } = data;
  
  // Call Render API - server handles Stripe AND Firestore updates
  const result = await callAPI('savePaymentMethod', { 
    setupIntentId,
    projectId 
  });
  
  return result;
};

// ============================================
// STAGE 1 PRE-AUTH HOLD FUNCTIONS
// ============================================

// Step 1 (Hold): Create PaymentIntent with manual capture (pre-authorisation)
// Server creates Stripe PaymentIntent AND updates Firestore (user + project)
export const createHoldIntent = async (data) => {
  const { projectId, userEmail, userName, amount, projectType, userId, townhouseUnits } = data;
  
  // Check if user already has a Stripe customer ID
  let existingCustomerId = null;
  if (userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists() && userDoc.data().stripeCustomerId) {
        existingCustomerId = userDoc.data().stripeCustomerId;
        console.log('Found existing customer ID:', existingCustomerId);
      }
    } catch (e) {
      console.log('Could not check for existing customer:', e.message);
    }
  }
  
  // Call Render API - server handles Stripe AND Firestore updates
  const result = await callAPI('createHoldIntent', {
    projectId,
    userEmail,
    userName,
    amount,
    projectType,
    userId,
    existingCustomerId,
    townhouseUnits
  });
  
  return result;
};

// Step 2 (Hold): Confirm hold after frontend confirms card
// Server verifies PaymentIntent status AND updates Firestore
export const confirmHold = async (data) => {
  const { paymentIntentId, projectId } = data;
  
  // Call Render API - server handles Stripe AND Firestore updates
  const result = await callAPI('confirmHold', {
    paymentIntentId,
    projectId
  });
  
  return result;
};

// Step 3 (Hold): Capture held funds or fall back to charging saved card
// Server attempts capture, falls back to charge, updates Firestore
export const captureHold = async (data) => {
  const { projectId } = data;
  
  // Call Render API - server handles Stripe AND Firestore updates
  const result = await callAPI('captureHold', {
    projectId
  });
  
  return result;
};

// Step 3: Charge customer (for auto-charge or manual payment)
// Server charges Stripe AND updates Firestore (payment status)
export const chargeCustomer = async (data) => {
  const { projectId, amount, isAutoCharge, customerId } = data;
  
  // Call Render API - server handles Stripe AND Firestore updates
  const result = await callAPI('chargeCustomer', {
    projectId,
    amount,
    isAutoCharge,
    customerId
  });
  
  return result;
};

// Get payment status from Firestore directly (faster than API call)
export const getPaymentStatus = async (data) => {
  const { projectId } = data;
  
  try {
    const projectDoc = await getDoc(doc(db, 'projects', projectId));
    
    if (!projectDoc.exists()) {
      throw new Error('Project not found');
    }
    
    const project = projectDoc.data();
    
    return {
      data: {
        paymentStatus: project.paymentStatus || 'pending',
        status: project.status || 'unknown',
        paymentMethod: project.paymentMethod || null,
        paidAt: project.paidAt || null,
        cardCapturedAt: project.cardCapturedAt || null
      }
    };
  } catch (error) {
    console.error('Error getting payment status:', error);
    throw error;
  }
};

// ============================================
// EXPORTS
// ============================================

export const isDevEnvironment = isDevelopment;
export const currentEnvironmentName = currentEnvironment;

export const debugConfig = () => {
  if (isDevelopment) {
    console.log('=== Firebase Configuration Debug ===');
    console.log('Environment:', currentEnvironment);
    console.log('Project ID:', firebaseConfig.projectId);
    console.log('API URL:', RENDER_API_URL);
    console.log('API Version: 3.3.0 (Pre-Auth Hold + Firebase Admin)');
    console.log('Stripe Key:', STRIPE_PUBLISHABLE_KEY.substring(0, 20) + '...');
    console.log('==================================');
  }
};

if (isDevelopment) {
  debugConfig();
}

export default app;